import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import Notification from '../components/Notification';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    // Verificar si hay un token y usuario guardados
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('solterosMexico_user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error al parsear usuario guardado:', error);
        localStorage.removeItem('solterosMexico_user');
        localStorage.removeItem('auth_token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (identifier, password, onSuccess) => {
    setIsLoggingIn(true);
    
    try {
      // Simular un pequeño delay para mejor UX
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Determinar si el identificador es un email o un nombre de usuario
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
      
      // Llamar al endpoint de login con el identificador apropiado
      const response = await apiService.login(identifier, password, isEmail ? 'email' : 'username');
      
      // Si el login no devuelve profilePicture, obtener usuario completo
      let completeUser = response.user;
      if (!response.user.profilePicture && !response.user.profile_picture) {
        try {
          completeUser = await apiService.getUserById(response.user.id);
        } catch (error) {
          console.warn('No se pudo obtener usuario completo, usando datos del login:', error);
          completeUser = response.user;
        }
      }
      
      // Transformar datos del backend al formato del frontend
      const userData = {
        id: completeUser.id,
        email: completeUser.email || identifier, // Usar el identificador del login si el backend no lo manda
        username: completeUser.username,
        name: completeUser.firstName || 'Usuario',
        age: completeUser.age || 25,
        gender: completeUser.sex === 'male' ? 'Hombre' : completeUser.sex === 'female' ? 'Mujer' : 'Hombre',
        state: completeUser.location || 'Ciudad de México',
        municipio: completeUser.municipio || '',
        description: completeUser.bio || '',
        hasPhoto: !!(completeUser.profilePicture || completeUser.profile_picture),
        photoUrl: completeUser.profilePicture || completeUser.profile_picture || null,
        isOnline: completeUser.isOnline === 1 || completeUser.isOnline === true,
        lastSeen: completeUser.lastLogin || completeUser.last_login || new Date().toISOString(),
        // Nuevos campos
        estadoCivil: completeUser.estadoCivil,
        sexoBusca: completeUser.sexoBusca,
        descripcionFisica: completeUser.descripcionFisica,
        educacion: completeUser.educacion,
        ocupacion: completeUser.ocupacion,
        tipoPersonaBusca: completeUser.tipoPersonaBusca
      };

      setUser(userData);
      localStorage.setItem('solterosMexico_user', JSON.stringify(userData));
      
      // Mostrar notificación de éxito
      setNotification({
        type: 'success',
        title: '¡Bienvenido!',
        message: `Hola ${userData.name}, has iniciado sesión correctamente`,
        duration: 4000
      });
      
      // Delay adicional para mostrar el éxito
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Llamar al callback de éxito si se proporciona
      if (onSuccess) {
        onSuccess(userData);
      }
      
      return userData;
    } catch (error) {
      throw new Error(error.message || 'Error en el login');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const register = async (userData, onSuccess) => {
    try {
      const response = await apiService.register(userData);
      
      // Si el registro es exitoso, hacer login automático
      if (response.success) {
        // Mostrar notificación de registro exitoso
        setNotification({
          type: 'success',
          title: '¡Registro exitoso!',
          message: response.message || 'Tu cuenta ha sido creada correctamente',
          duration: 4000
        });
        
        // Retornar el mensaje del backend para mostrarlo al usuario
        const loginResult = await login(userData.email, userData.password, onSuccess);
        return {
          ...loginResult,
          registrationMessage: response.message
        };
      }
      
      return response;
    } catch (error) {
      throw new Error(error.message || 'Error en el registro');
    }
  };

  const logout = async () => {
    setIsLoggingOut(true);
    
    try {
      // Mostrar feedback visual antes del logout
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Llamar al endpoint de logout del backend
      await apiService.logout();
    } catch (error) {
      console.error('Error en logout del servidor:', error);
      // Continuar con logout local aunque falle el servidor
    }
    
    // Limpiar estado local con delay
    await new Promise(resolve => setTimeout(resolve, 400));
    setUser(null);
    localStorage.removeItem('solterosMexico_user');
    // El token ya se limpia en apiService.logout()
    
    // Mostrar notificación de logout
    setNotification({
      type: 'info',
      title: 'Sesión cerrada',
      message: 'Has cerrado sesión correctamente',
      duration: 3000
    });
    
    // Redirigir a la página de inicio
    window.location.href = '/';
    
    setIsLoggingOut(false);
  };

  const refreshUserData = async () => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    try {
      const freshUserData = await apiService.getUserById(user.id);
      
      // Transformar datos del backend al formato del frontend
      const userData = {
        id: freshUserData.id,
        email: freshUserData.email || user.email, // Mantener el email existente si el backend no lo manda
        username: freshUserData.username,
        name: freshUserData.firstName || 'Usuario',
        age: freshUserData.age || 25,
        gender: freshUserData.sex === 'male' ? 'Hombre' : freshUserData.sex === 'female' ? 'Mujer' : 'Hombre',
        state: freshUserData.location || 'Ciudad de México',
        municipio: freshUserData.municipio || '',
        description: freshUserData.bio || '',
        hasPhoto: !!(freshUserData.profilePicture || freshUserData.profile_picture),
        photoUrl: freshUserData.profilePicture || freshUserData.profile_picture || null,
        isOnline: freshUserData.isOnline === 1 || freshUserData.isOnline === true,
        lastSeen: freshUserData.lastLogin || freshUserData.last_login || new Date().toISOString(),
        // Nuevos campos
        estadoCivil: freshUserData.estadoCivil,
        sexoBusca: freshUserData.sexoBusca,
        descripcionFisica: freshUserData.descripcionFisica,
        educacion: freshUserData.educacion,
        ocupacion: freshUserData.ocupacion,
        tipoPersonaBusca: freshUserData.tipoPersonaBusca
      };

      setUser(userData);
      localStorage.setItem('solterosMexico_user', JSON.stringify(userData));
      
      return userData;
    } catch (error) {
      console.error('Error refrescando datos del usuario:', error);
      throw new Error('Error al actualizar los datos del usuario');
    }
  };

  const updateProfile = async (updatedData) => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    try {
      // Si solo se está actualizando la imagen, no hacer llamada al backend
      if (updatedData.photoUrl !== undefined || updatedData.hasPhoto !== undefined) {
        const updatedUser = { ...user, ...updatedData };
        setUser(updatedUser);
        localStorage.setItem('solterosMexico_user', JSON.stringify(updatedUser));
        
        // Mostrar notificación de éxito
        setNotification({
          type: 'success',
          title: 'Perfil actualizado',
          message: 'Tus datos han sido actualizados correctamente',
          duration: 4000
        });
        
        return;
      }

      // Para otros campos, sí hacer llamada al backend
      const response = await apiService.updateUser(user.id, updatedData);
      
      // Si el backend devuelve el usuario completo actualizado, usarlo
      if (response.user) {
        const transformedUser = {
          id: response.user.id,
          email: response.user.email || user.email, // Mantener el email existente si el backend no lo manda
          username: response.user.username,
          name: response.user.firstName || 'Usuario',
          age: response.user.age,
          gender: response.user.sex === 'male' ? 'Hombre' : response.user.sex === 'female' ? 'Mujer' : 'Hombre',
          state: response.user.location || 'Ciudad de México',
          municipio: response.user.municipio || '',
          description: response.user.bio || '',
          hasPhoto: !!(response.user.profilePicture || response.user.profile_picture),
          photoUrl: response.user.profilePicture || response.user.profile_picture || null,
          isOnline: response.user.isOnline === 1 || response.user.isOnline === true,
          lastSeen: response.user.lastLogin || response.user.last_login || new Date().toISOString(),
          // Nuevos campos
          estadoCivil: response.user.estadoCivil,
          sexoBusca: response.user.sexoBusca,
          descripcionFisica: response.user.descripcionFisica,
          educacion: response.user.educacion,
          ocupacion: response.user.ocupacion,
          tipoPersonaBusca: response.user.tipoPersonaBusca
        };
        
        setUser(transformedUser);
        localStorage.setItem('solterosMexico_user', JSON.stringify(transformedUser));
        
        // Mostrar notificación de éxito
        setNotification({
          type: 'success',
          title: 'Perfil actualizado',
          message: 'Tus datos han sido actualizados correctamente',
          duration: 4000
        });
      } else {
        // Fallback: actualizar solo los campos modificados
        const updatedUser = { ...user, ...updatedData };
        setUser(updatedUser);
        localStorage.setItem('solterosMexico_user', JSON.stringify(updatedUser));
        
        // Mostrar notificación de éxito
        setNotification({
          type: 'success',
          title: 'Perfil actualizado',
          message: 'Tus datos han sido actualizados correctamente',
          duration: 4000
        });
      }
    } catch (error) {
      throw new Error(error.message || 'Error al actualizar perfil');
    }
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateProfile,
    refreshUserData,
    loading,
    isLoggingIn,
    isLoggingOut,
    notification,
    closeNotification
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Notification notification={notification} onClose={closeNotification} />
    </AuthContext.Provider>
  );
};
